import { Chromosome } from '../types/algorithm';
import { calculateChromosomeFitness } from '../utils/fitnessCalculator';

/**
 * Genetic Algorithm implementation from scratch
 * Based on standard GA principles: Selection, Crossover, Mutation, Elitism
 */

/**
 * Creates initial population
 */
export function initializePopulation(
  populationSize: number,
  taskCount: number,
  serviceCount: number
): Chromosome[] {
  const population: Chromosome[] = [];

  for (let i = 0; i < populationSize; i++) {
    const chromosome: Chromosome = {
      x: new Array(taskCount),
      fitness: 0,
      rwp: 0,
    };

    // Random initialization
    for (let j = 0; j < taskCount; j++) {
      chromosome.x[j] = Math.floor(Math.random() * serviceCount);
    }

    population.push(chromosome);
  }

  return population;
}

/**
 * Calculates relative weight probability for roulette wheel selection
 */
export function calculateRWP(population: Chromosome[]): void {
  let totalFitness = 0;

  // Calculate total fitness
  for (const chrom of population) {
    totalFitness += chrom.fitness;
  }

  // Avoid division by zero
  if (totalFitness === 0) {
    const equalProb = 1 / population.length;
    for (const chrom of population) {
      chrom.rwp = equalProb;
    }
    return;
  }

  // Calculate relative probabilities
  for (const chrom of population) {
    chrom.rwp = chrom.fitness / totalFitness;
  }
}

/**
 * Roulette wheel selection
 */
export function rouletteWheelSelection(population: Chromosome[]): number {
  const r = Math.random();
  let sum = 0;

  for (let i = 0; i < population.length; i++) {
    sum += population[i].rwp;
    if (sum >= r) {
      return i;
    }
  }

  // Fallback to last element
  return population.length - 1;
}

/**
 * Single-point crossover
 * Creates two offspring from two parents
 */
export function singlePointCrossover(
  parent1: Chromosome,
  parent2: Chromosome,
  taskCount: number
): [Chromosome, Chromosome] {
  // Random crossover point
  const point = Math.floor(Math.random() * (taskCount - 1)) + 1;

  const child1: Chromosome = {
    x: [...parent1.x.slice(0, point), ...parent2.x.slice(point)],
    fitness: 0,
    rwp: 0,
  };

  const child2: Chromosome = {
    x: [...parent2.x.slice(0, point), ...parent1.x.slice(point)],
    fitness: 0,
    rwp: 0,
  };

  return [child1, child2];
}

/**
 * Mutation operator - random gene mutation
 */
export function mutate(
  chromosome: Chromosome,
  taskCount: number,
  serviceCount: number
): Chromosome {
  const mutated: Chromosome = {
    x: [...chromosome.x],
    fitness: 0,
    rwp: 0,
  };

  // Random mutation point (skip first task to maintain some structure)
  const point = Math.floor(Math.random() * (taskCount - 1)) + 1;
  mutated.x[point] = Math.floor(Math.random() * serviceCount);

  return mutated;
}

/**
 * Main GA evolution step
 */
export function evolveGA(
  population: Chromosome[],
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  selectionRate: number,
  crossoverRate: number,
  mutationRate: number
): void {
  const popSize = population.length;
  const selectCount = Math.floor((popSize * selectionRate) / 100);
  const crossoverCount = Math.floor((popSize * crossoverRate) / 100);
  const mutateCount = Math.floor((popSize * mutationRate) / 100);

  // Calculate RWP for selection
  calculateRWP(population);

  // Crossover operations
  for (let i = 0; i < crossoverCount; i += 2) {
    let parent1Idx: number;
    let parent2Idx: number;

    // Use roulette wheel for first selectCount crossovers, then random
    if (i < selectCount) {
      parent1Idx = rouletteWheelSelection(population);
      parent2Idx = rouletteWheelSelection(population);
    } else {
      parent1Idx = Math.floor(Math.random() * popSize);
      parent2Idx = Math.floor(Math.random() * popSize);
    }

    const [child1, child2] = singlePointCrossover(
      population[parent1Idx],
      population[parent2Idx],
      taskCount
    );

    // Evaluate offspring
    calculateChromosomeFitness(child1, matrix, taskCount, serviceCount);
    calculateChromosomeFitness(child2, matrix, taskCount, serviceCount);

    // Replace parents if offspring are better (greedy replacement)
    if (child1.fitness > population[parent1Idx].fitness) {
      population[parent1Idx] = child1;
    }
    if (child2.fitness > population[parent2Idx].fitness && i + 1 < crossoverCount) {
      population[parent2Idx] = child2;
    }
  }

  // Mutation operations
  for (let i = 0; i < mutateCount; i++) {
    const idx = Math.floor(Math.random() * popSize);
    const mutated = mutate(population[idx], taskCount, serviceCount);
    calculateChromosomeFitness(mutated, matrix, taskCount, serviceCount);

    // Replace if better
    if (mutated.fitness > population[idx].fitness) {
      population[idx] = mutated;
    }
  }
}

/**
 * Finds best chromosome in population
 */
export function findBestChromosome(population: Chromosome[]): Chromosome {
  let best = population[0];
  for (const chrom of population) {
    if (chrom.fitness > best.fitness) {
      best = chrom;
    }
  }
  return best;
}

/**
 * Runs GA for specified generations
 */
export async function runGeneticAlgorithm(
  taskCount: number,
  serviceCount: number,
  matrix: number[][][],
  parameters: {
    populationSize: number;
    generations: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  },
  onGenerationComplete?: (generation: number, bestFitness: number) => void
): Promise<Chromosome> {
  // Initialize population
  let population = initializePopulation(
    parameters.populationSize,
    taskCount,
    serviceCount
  );

  // Evaluate initial population
  for (const chrom of population) {
    calculateChromosomeFitness(chrom, matrix, taskCount, serviceCount);
  }

  // Evolution loop
  for (let gen = 0; gen < parameters.generations; gen++) {
    evolveGA(
      population,
      taskCount,
      serviceCount,
      matrix,
      parameters.selectionRate,
      parameters.crossoverRate,
      parameters.mutationRate
    );

    // Report progress
    const best = findBestChromosome(population);
    if (onGenerationComplete) {
      onGenerationComplete(gen + 1, best.fitness);
    }

    // Yield control to allow UI updates
    await new Promise(resolve => setTimeout(resolve, 0));
  }

  return findBestChromosome(population);
}

